import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PackagingApiAdapter } from '../adapters/PackagingApiAdapter';
import { CreatePackagingRequest } from '../models/Packaging';
import { PackagingService } from '../services/PackagingService';

interface UseCreatePackagingOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useCreatePackaging = (options: UseCreatePackagingOptions = {}) => {
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new PackagingApiAdapter(httpClient);
  const packagingService = new PackagingService(apiAdapter);
  const { toast } = useToast();
  const { t } = useTranslation('materialAlmacenModule');

  const mutation = useMutation({
    mutationFn: (data: CreatePackagingRequest) => packagingService.createPackaging(data),
    onSuccess: () => {
      // Invalidar queries de packaging para refrescar el listado
      queryClient.invalidateQueries({ queryKey: ['materialAlmacen'] });
      queryClient.invalidateQueries({ queryKey: ['activeOwnBoxes'] });
      queryClient.invalidateQueries({ queryKey: ['activeRates'] });

      toast({
        title: t('messages.create_success'),
        description: t('modal.create.success_rates_description'),
        variant: 'default',
      });

      options.onSuccess?.();
    },
    onError: (error: any) => {
      console.log('🔍 useCreatePackaging - Error:', error);

      const errorCode = error?.response?.data?.errorCode || error?.response?.data?.error?.error_code;
      let errorMessage = error?.message || t('errors.unexpected');

      if (errorCode === 'OWN_BOX_REQUIRES_RATE_PRICES') {
        errorMessage = t('errors.own_box_requires_rate_prices');
      } else if (errorCode === 'OWN_BOX_RATE_PRICES_INCOMPLETE') {
        errorMessage = t('errors.own_box_rate_prices_incomplete');
      } else if (errorCode === 'OWN_BOX_MISSING_PACKAGING_ON_ACTIVE_RATES') {
        errorMessage = t('errors.own_box_missing_packaging_on_active_rates');
      } else if (error?.response?.status === 422 && error?.response?.data?.errors?.rates) {
        const ratesErrors = error.response.data.errors.rates;
        errorMessage = Array.isArray(ratesErrors) ? ratesErrors[0] : ratesErrors;
      }

      toast({
        title: t('messages.create_error'),
        description: errorMessage,
        variant: 'destructive',
      });

      options.onError?.(error);
    },
  });

  return {
    createPackaging: mutation.mutate,
    createPackagingAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
};
